import type { LocalDate } from './types';

const DAY_MS = 86_400_000;

interface DateParts {
  year: number;
  month: number;
  day: number;
}

export function assertLocalDate(value: string): asserts value is LocalDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new Error(`Invalid local date: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1000 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    throw new Error(`Invalid local date: ${value}`);
  }
}

export function localDateParts(value: LocalDate): DateParts {
  assertLocalDate(value);
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid local date: ${value}`);
  }
  return { year, month, day };
}

export function parseLocalDate(value: LocalDate): Date {
  const { year, month, day } = localDateParts(value);
  return new Date(Date.UTC(year, month - 1, day));
}

export function toLocalDate(date: Date): LocalDate {
  if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
  return date.toISOString().slice(0, 10) as LocalDate;
}

export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  assertLocalDate(a);
  assertLocalDate(b);
  return a.localeCompare(b);
}

export function minLocalDate(a: LocalDate, b: LocalDate): LocalDate {
  return compareLocalDates(a, b) <= 0 ? a : b;
}

export function maxLocalDate(a: LocalDate, b: LocalDate): LocalDate {
  return compareLocalDates(a, b) >= 0 ? a : b;
}

export function addDays(date: LocalDate, count: number): LocalDate {
  if (!Number.isSafeInteger(count)) throw new Error('Day count must be a safe integer');
  const parsed = parseLocalDate(date);
  parsed.setUTCDate(parsed.getUTCDate() + count);
  return toLocalDate(parsed);
}

export function addMonths(date: LocalDate, count: number): LocalDate {
  if (!Number.isSafeInteger(count)) throw new Error('Month count must be a safe integer');
  const parsed = parseLocalDate(date);
  const day = parsed.getUTCDate();
  parsed.setUTCDate(1);
  parsed.setUTCMonth(parsed.getUTCMonth() + count);
  const maxDay = daysInMonth(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1);
  parsed.setUTCDate(Math.min(day, maxDay));
  return toLocalDate(parsed);
}

export function startOfMonth(date: LocalDate): LocalDate {
  const { year, month } = localDateParts(date);
  return makeLocalDate(year, month, 1);
}

export function endOfMonth(date: LocalDate): LocalDate {
  const { year, month } = localDateParts(date);
  return makeLocalDate(year, month, daysInMonth(year, month));
}

export function monthKey(date: LocalDate): string {
  assertLocalDate(date);
  return date.slice(0, 7);
}

export function monthsInclusive(from: LocalDate, to: LocalDate): number {
  if (compareLocalDates(from, to) > 0) return 0;
  const start = localDateParts(from);
  const end = localDateParts(to);
  const difference = (end.year - start.year) * 12 + end.month - start.month;
  return Math.max(1, difference + (end.day >= start.day ? 1 : 0));
}

export function differenceInCalendarDays(from: LocalDate, to: LocalDate): number {
  return Math.trunc((parseLocalDate(to).getTime() - parseLocalDate(from).getTime()) / DAY_MS);
}

export function daysInMonth(year: number, month: number): number {
  if (!Number.isSafeInteger(year) || !Number.isSafeInteger(month) || month < 1 || month > 12) {
    throw new Error('Invalid year or month');
  }
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function makeLocalDate(year: number, month: number, day: number): LocalDate {
  const value = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  assertLocalDate(value);
  return value;
}

export function contributionDateForMonth(monthDate: LocalDate, dayOfMonth: number): LocalDate {
  if (!Number.isSafeInteger(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
    throw new Error('Contribution day must be from 1 through 31');
  }
  const { year, month } = localDateParts(monthDate);
  return makeLocalDate(year, month, Math.min(dayOfMonth, daysInMonth(year, month)));
}

export function contributionDates(from: LocalDate, to: LocalDate, dayOfMonth: number): LocalDate[] {
  if (compareLocalDates(from, to) > 0) return [];
  const result: LocalDate[] = [];
  for (
    let cursor = startOfMonth(from);
    compareLocalDates(cursor, to) <= 0;
    cursor = addMonths(cursor, 1)
  ) {
    const candidate = contributionDateForMonth(cursor, dayOfMonth);
    if (compareLocalDates(candidate, from) >= 0 && compareLocalDates(candidate, to) <= 0) {
      result.push(candidate);
    }
  }
  return result;
}
